/* eslint-disable complexity */
import { yupResolver } from "@hookform/resolvers/yup";
import { useNotify } from "@imminently/react/store/notifications";
import { Text } from "@imminently/react/ui/core/text";
import { Stack, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { OpenInFull as MaximiseIcon, CloseFullscreen as MinimiseIcon } from "@mui/icons-material";
import React from "react";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import SplitPane from "react-split-pane";
import { PatientEncounter } from "@resources/types";
import { useNoteService } from "@services";
import { Icon } from "@components/Icon";
import { css } from "@theme";
import { WORK_OFFLINE, ENABLE_AI_NOTES } from "@/resources/constants";
import global from "@/global";
import {
  FormFields,
  NOTE_TEMPLATES
} from "./PatientNotesConstants";
import { PatientNotesFormPane } from "./PatientNotesFormPane";
import { PatientNotesAIDrawer, AutoStartState } from "./PatientNotesAIDrawer";

const schema = Yup.object<FormFields>().shape({
  text: Yup.string().required("Required"),
  template: Yup.string().optional(),
  title: Yup.string().optional(),
});

interface PatientNotesProps {
  encounter: PatientEncounter;
}

export const PatientNotes = ({ encounter }: PatientNotesProps) => {
  const { error } = useNotify();
  const { useCreate } = useNoteService(encounter.id);

  const { control, handleSubmit, reset, setValue, watch, getValues } = useForm<FormFields>({
    defaultValues: { template: "", text: "", title: "" },
    resolver: yupResolver(schema) as any,
  });

  const { mutateAsync, isLoading } = useCreate();
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(true);
  const [containerWidth, setContainerWidth] = React.useState<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [leftPaneSize, setLeftPaneSize] = React.useState<number | null>(null);
  const [threadId, setThreadId] = React.useState<string | null>(null);
  const [assistantId, setAssistantId] = React.useState<string | null>(null);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [draftBeforeExpand, setDraftBeforeExpand] = React.useState<FormFields | null>(null);

  // Layout constants
  const LEFT_MIN = 520; // px
  const RIGHT_MIN = 200; // px

  const currentText = watch("text");
  const currentTemplate = watch("template");

  const aiEnabled = ENABLE_AI_NOTES;

  // Auto-start state management
  const [autoStartState, setAutoStartState] = React.useState<AutoStartState>({
    defaultChatPhraseIsDefault: true,
    conversationStarted: false,
    templateChanges: 0,
    noTemplateCurrentlySelected: true,
  });
  const [autoStartTick, setAutoStartTick] = React.useState(0);
  const prevTemplateRef = React.useRef<string | null>(null);

  const handleTemplateSelect = React.useCallback((template: typeof NOTE_TEMPLATES[number] | null) => {
    if (template) {
      setValue("title", template.title);
      setValue("text", template.tpl);
      // Detect none -> some transition (first selection only)
      const wasNone = !prevTemplateRef.current || prevTemplateRef.current === "";
      prevTemplateRef.current = template.id;
      setAutoStartState(prev => ({
        ...prev,
        templateChanges: prev.templateChanges + 1,
        noTemplateCurrentlySelected: false,
      }));
      if (wasNone && isDrawerOpen && !autoStartState.conversationStarted && autoStartState.defaultChatPhraseIsDefault) {
        setAutoStartTick(n => n + 1);
      }
    } else {
      setValue("title", "");
      setValue("text", "");
      prevTemplateRef.current = null;
      setAutoStartState(prev => ({
        ...prev,
        noTemplateCurrentlySelected: true,
      }));
    }
  }, [setValue, isDrawerOpen, autoStartState.conversationStarted, autoStartState.defaultChatPhraseIsDefault]);

  const handleAiGenerate = React.useCallback(async (
    userMessage: string,
    _chatHistory: Array<{ role: "user" | "assistant"; content: string }>
  ): Promise<string> => {
    // mark as intentionally unused (API requires this param shape)
    void _chatHistory;
    try {
      setIsGenerating(true);

      const selectedTemplate = NOTE_TEMPLATES.find(t => t.id === currentTemplate);

      // Call backend API - it will load encounter and add context automatically
      const response = await global.client.post(
        `encounters/${encounter.id}/notes/generate`,
        {
          userMessage,
          currentText,
          template: selectedTemplate?.tpl,
          threadId: threadId,
          assistantId: assistantId,
          // If working offline with mock encounters, allow overriding the backend resource
          encounterOverride: (WORK_OFFLINE ? encounter : undefined) as any,
        }
      );

      if (!response.data || !response.data.text) {
        throw new Error("Invalid response from AI service");
      }

      // Store threadId and assistantId for ongoing conversation
      if (response.data.threadId) {
        setThreadId(response.data.threadId);
      }
      if (response.data.assistantId) {
        setAssistantId(response.data.assistantId);
      }

      return response.data.text;
    } catch (e: any) {
      error(e?.response?.data?.message || "Failed to generate with AI");
      throw e;
    } finally {
      setIsGenerating(false);
    }
  }, [currentText, currentTemplate, encounter.id, error, threadId, assistantId]);

  const handleReplaceText = React.useCallback((text: string) => {
    setValue("text", text);
  }, [setValue]);

  const handleInsertBelow = React.useCallback((text: string) => {
    const current = currentText || "";
    setValue("text", current ? `${current}\n\n${text}` : text);
  }, [currentText, setValue]);

  //#region SplitPane Resize Management
  // Measure container width helper - used to constrain SplitPane to prevent layout overflow
  const updateContainerWidth = React.useCallback(() => {
    if (containerRef.current) {
      const width = containerRef.current.getBoundingClientRect().width;
      // Only update if the width actually changed (prevent feedback loops)
      setContainerWidth(prev => {
        if (Math.abs((prev || 0) - width) < 10) {
          return prev; // No significant change
        }
        return width;
      });
    }
  }, []);

  // Observe container size changes (more robust than window resize)
  // Handles iPad orientation flips and window resizing without layout jumps
  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    // Initial measurement
    updateContainerWidth();

    let timeoutId: number | undefined;
    const observer = new ResizeObserver(() => {
      if (timeoutId) window.clearTimeout(timeoutId);
      // Debounce a bit slower; typical change is orientation flips, not constant resizing
      timeoutId = window.setTimeout(updateContainerWidth, 600);
    });
    observer.observe(node);

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [updateContainerWidth, isExpanded]);

  // Clamp left pane whenever container width or drawer state changes
  // Prevents SplitPane from expanding beyond container boundaries
  React.useEffect(() => {
    if (!containerWidth) return;

    if (!isDrawerOpen) {
      setLeftPaneSize(containerWidth);
      return;
    }

    const maxLeft = Math.max(LEFT_MIN, containerWidth - RIGHT_MIN);
    const desired = leftPaneSize ?? Math.round(containerWidth * 0.5);
    const clamped = Math.min(Math.max(desired, LEFT_MIN), maxLeft);
    setLeftPaneSize(clamped);
  }, [containerWidth, isDrawerOpen]);

  // Handle SplitPane drag updates with clamping
  // Ensures user can't drag resizer beyond min/max constraints
  const handleSplitChange = React.useCallback((size: number) => {
    if (!containerWidth) {
      setLeftPaneSize(size);
      return;
    }
    const maxLeft = Math.max(LEFT_MIN, containerWidth - RIGHT_MIN);
    const clamped = Math.min(Math.max(size, LEFT_MIN), maxLeft);
    setLeftPaneSize(clamped);
  }, [containerWidth]);
  //#endregion

  const persistNote = React.useCallback(async (data: FormFields) => {
    const note: NonNullable<PatientEncounter["note"]> = {
      text: data.text,
      title: data.title,
    };

    try {
      await mutateAsync(note);
      reset();
      return true;
    } catch {
      error("Failed to create note");
      return false;
    }
  }, [mutateAsync, reset, error]);

  const submitHandler = React.useCallback(
    handleSubmit(async (data) => {
      await persistNote(data);
    }),
    [handleSubmit, persistNote]
  );

  const submitAndCloseHandler = React.useCallback(
    handleSubmit(async (data) => {
      const didSave = await persistNote(data);
      if (didSave) {
        setIsExpanded(false);
        setDraftBeforeExpand(null);
      }
    }),
    [handleSubmit, persistNote]
  );

  const handleMaximise = React.useCallback(() => {
    setDraftBeforeExpand(getValues());
    setIsExpanded(true);
  }, [getValues]);

  const handleMinimise = React.useCallback(() => {
    setIsExpanded(false);
  }, []);

  const handleExpandedCancel = React.useCallback(() => {
    if (draftBeforeExpand) {
      reset(draftBeforeExpand);
    }
    setIsExpanded(false);
    setDraftBeforeExpand(null);
  }, [draftBeforeExpand, reset]);

  const renderNotesEditor = (expanded: boolean) => (
    <Box
      ref={containerRef}
      position="relative"
      height={expanded ? "100%" : "auto"}
      css={css`
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        overflow: hidden;

        .Resizer {
          background: #e0e0e0;
          opacity: 0.5;
          z-index: 1;
          box-sizing: border-box;
          background-clip: padding-box;
        }

        .Resizer:hover {
          transition: all 0.2s ease;
          opacity: 1;
        }

        .Resizer.horizontal {
          height: 11px;
          margin: -5px 0;
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          cursor: row-resize;
          width: 100%;
        }

        .Resizer.vertical {
          width: 11px;
          margin: 0 -5px;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          cursor: col-resize;
        }

        .Resizer.disabled {
          cursor: not-allowed;
        }

        .Resizer.disabled:hover {
          border-color: transparent;
        }
      `}
    >
      {aiEnabled ? (
        // @ts-expect-error - react-split-pane has incomplete type definitions
        <SplitPane
          split="vertical"
          minSize={LEFT_MIN}
          maxSize={containerWidth ? containerWidth - RIGHT_MIN : undefined}
          size={isDrawerOpen ? (leftPaneSize ?? LEFT_MIN) : "100%"}
          pane1Style={{ position: "relative" }}
          pane2Style={
            isDrawerOpen
              ? { display: "block", minWidth: `${RIGHT_MIN}px` }
              : { display: "none" }
          }
          style={{
            position: "relative",
            width: containerWidth ? `${containerWidth}px` : "100%",
            maxWidth: "100%"
          }}
          onChange={handleSplitChange}
        >
          <Box position="relative" height="100%">
            <PatientNotesFormPane
              control={control as any}
              isLoading={isLoading}
              isGenerating={isGenerating}
              onTemplateSelect={handleTemplateSelect}
              isExpanded={expanded}
            />

            {/* AI Drawer Toggle Button */}
            {!isDrawerOpen && (
              <Button
                variant="outlined"
                onClick={() => {
                  if (containerWidth) {
                    const maxLeft = Math.max(LEFT_MIN, containerWidth - RIGHT_MIN);
                    const desired = Math.round(containerWidth * 0.5);
                    const clamped = Math.min(Math.max(desired, LEFT_MIN), maxLeft);
                    setLeftPaneSize(clamped);
                  }
                  setIsDrawerOpen(true);
                }}
                sx={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  minWidth: "40px",
                  height: "80px",
                  borderRadius: "8px 0 0 8px",
                  borderRight: "none",
                  padding: "8px 4px",
                  writingMode: "vertical-rl",
                  fontSize: "12px",
                  zIndex: 5,
                  backgroundColor: "background.paper",
                }}
                startIcon={<Icon icon="build" style={{ margin: 0 }} />}
              >
                AI
              </Button>
            )}
          </Box>
          <Box height="100%">
            <PatientNotesAIDrawer
              isOpen={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
              onReplace={handleReplaceText}
              onInsertBelow={handleInsertBelow}
              currentText={currentText}
              onGenerate={handleAiGenerate}
              isGenerating={isGenerating}
              autoStartTick={autoStartTick}
              autoStartState={autoStartState}
              onConversationStarted={() => setAutoStartState(prev => ({ ...prev, conversationStarted: true }))}
              onDefaultPromptStateChange={React.useCallback((isDefault: boolean) => {
                setAutoStartState(prev => (
                  prev.defaultChatPhraseIsDefault === isDefault
                    ? prev
                    : { ...prev, defaultChatPhraseIsDefault: isDefault }
                ));
              }, [])}
            />
          </Box>
        </SplitPane>
      ) : (
        <Box position="relative" height="100%">
          <PatientNotesFormPane
            control={control as any}
            isLoading={isLoading}
            isGenerating={false}
            onTemplateSelect={handleTemplateSelect}
            isExpanded={expanded}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Text variant="h4">Waiting room notes</Text>
        <Button
          variant="outlined"
          onClick={handleMaximise}
          disabled={isLoading || isGenerating}
          startIcon={<MaximiseIcon />}
        >
          Maximise
        </Button>
      </Stack>

      {!isExpanded && renderNotesEditor(false)}

      {/* button bar */}
      {!isExpanded && (
        <Stack gap={2} direction="row" justifyContent="flex-end">
          <Button disabled={isLoading || isGenerating} variant="text" onClick={() => reset()}>
            <Text variant="buttonSmall">Clear</Text>
          </Button>
          <Button
            disabled={isLoading || isGenerating}
            variant="contained"
            onClick={submitHandler}
          >
            Submit note
          </Button>
        </Stack>
      )}

      <Dialog
        open={isExpanded}
        onClose={handleMinimise}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "95vw",
            maxWidth: "95vw",
            height: "90vh",
            maxHeight: "90vh",
          }
        }}
      >
        <DialogTitle>Waiting room notes</DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {renderNotesEditor(true)}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleExpandedCancel}
            disabled={isLoading || isGenerating}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleMinimise}
            disabled={isLoading || isGenerating}
            variant="outlined"
            startIcon={<MinimiseIcon />}
          >
            Minimise
          </Button>
          <Button onClick={submitAndCloseHandler} variant="contained" disabled={isLoading || isGenerating}>
            Save note
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
